import React, { useState, useEffect, useRef } from "react";
import MerchantLayout from "src/components/Layouts/MerchantLayout";
import { NormalText } from "src/components/styled/text.styled";
import { Button } from "src/components/styled/button.styled";
import Tables from "src/components/Table/tableData";
import { Box, Stack, Grid, InputBase, Typography, Autocomplete, TextField, Tooltip } from "@mui/material";
import InputDate from "src/components/Inputs/DatePicker";
import { styled, alpha } from "@mui/material/styles";
import OrderGroups from "../layout";
import { formatCurrency, getToken } from "src/helper/generalFunction";
import useRedux from "src/redux/useRedux";
import { getList, ClosingResponseProps, ClosingProps } from "src/redux/closingCashierReport";
import { getAllStore, ListStoreData } from "src/redux/storelist";
import moment from "moment";
import "moment/locale/id";
import axios, { AxiosRequestConfig, CancelTokenSource } from "axios";
import { toast } from "react-toastify";
import FilterStore from "src/views/Order/storeFilter";

moment.locale("id");

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "5px",
  border: "1px solid #aaa",
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "230px",
  height: "45px",
  marginTop: "10px",
  [theme.breakpoints.down("sm")]: {
    marginTop: "10px",
    maxWidth: "unset",
    width: "230px",
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    fontSize: "14px",
    padding: theme.spacing(1, 1, 1, 1),
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "unset",
      width: "100%",
    },
  },
}));

const TransactionReportPage = () => {
  const {
    thunkDispatch,
    storeState: {
      Auth: {
        status,
        data: { user_type, profile_data },
      },
      AppOption: { selected_store },
    },
  } = useRedux();
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [filter, setFilter] = useState("");
  const [filterStore, setFilterStore] = useState<{ id: number; store_name: string }>({ id: 0, store_name: "" });
  const [filterCashier, setFilterCashier] = useState("ALL_CASHIERS");
  const [stores, setStores] = useState<ListStoreData[]>([]);
  const [availableCashiers, setAvailableCashiers] = useState<string[]>([]);
  const [transactions, setTransactions] = useState<ClosingResponseProps>();
  const [filterPayload, setFilterPayload] = useState<{
    start_date?: string;
    end_date?: string;
  }>({
    start_date: undefined,
    end_date: undefined,
  });
  const [download, setDownload] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  useEffect(() => {
    thunkDispatch(getAllStore({ limit: 0, offset: 0 }))
      .unwrap()
      .then((res) => {
        if (res && res.status === "success") {
          setStores(res.data.data);
        }
      })
      .catch((err) => {
        console.error("Error loading stores:", err);
        toast.error("Error loading stores");
      });
  }, [thunkDispatch]);

  const loadCashierData = async (storeId: number | null, cancelToken: CancelTokenSource) => {
    try {
      const config = {
        headers: {
          api_key: `${process.env.REACT_APP_API_KEY}`,
          token: getToken(),
        },
        cancelToken: cancelToken.token,
      };

      let url = `/merchant/close-pos/report?limit=100&offset=0`;
      if (storeId && storeId !== 0) {
        url += `&store_id=${storeId}`;
      }

      const response = await axios.get<{ data: ClosingProps[] }>(
        `${process.env.REACT_APP_API_URL}${url}`,
        config
      );

      if (response.data && response.data.data) {
        const cashiers: string[] = Array.from(
          new Set(response.data.data.filter(item => item.cashier_name).map(item => item.cashier_name))
        );
        setAvailableCashiers(cashiers);
      } else {
        setAvailableCashiers([]);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Cashier data fetch cancelled");
        return;
      }
      console.error("Error loading cashier data:", error);
      toast.error("Error loading cashier data");
      setAvailableCashiers([]);
    }
  };

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    setAvailableCashiers([]);

    setFilterCashier("ALL_CASHIERS");
    loadCashierData(filterStore?.id || null, cancelToken);

    return () => {
      cancelToken.cancel("Cancelled due to new store selection");
    };
  }, [filterStore?.id]);

  const fetchTransactions = () => {
    setLoading(true);
    const storeId = user_type === "merchant" 
    ? filterStore?.id 
      : (selected_store?.id ?? 0);
    
    const params: any = {
      end_date: filterPayload.end_date
        ? moment(filterPayload.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : "",
      limit: limit,
      offset: offset,
      start_date: filterPayload.start_date
        ? moment(filterPayload.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : null,
      filter: filter,
      cashier_name: filterCashier === "ALL_CASHIERS" ? "" : filterCashier,
    };
    if (storeId && storeId !== 0) {
      params.store_id = String(storeId);
    }

    thunkDispatch(getList(params))
      .unwrap()
      .then((res) => {
        if (res && res.status === "success") {
          setLoading(false);
          setTransactions(res.data);
            if (res.data.data.length === 0) {
              if (filterStore && filterStore.id !== 0 && availableCashiers.length === 0) {
                toast.warning(`Toko "${filterStore.store_name}" tidak memiliki data kasir.`);
              } 
              else if (filterCashier && filterCashier !== "ALL_CASHIERS") {
                toast.warning(`Tidak ada data untuk kasir "${filterCashier}" pada tanggal yang dipilih.`);
              } 
              else {
                toast.warning("Tidak ada data untuk tanggal yang dipilih.");
              }
            }
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error fetching transactions:", err);
        toast.error("Error fetching transactions");
      });
  };

  const handleExport = async () => {
    setDownload(true);
    const headers = {
      "Content-Type": "blob",
      api_key: `${process.env.REACT_APP_API_KEY}`,
      token: getToken(),
    };
    let startDate = filterPayload.start_date
      ? moment(filterPayload.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
      : null;
    let endDate = filterPayload.end_date
      ? moment(filterPayload.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
      : null;
    let storeId = String(filterStore?.id ?? "");
    if (status && status === "success" && user_type && user_type !== "merchant") {
      storeId = String(selected_store && selected_store.id ? selected_store.id : "");
    }
    let url = `/merchant/close-pos/report/download?${
      startDate ? `start_date=${startDate}` : ""
    }${endDate ? `&end_date=${endDate}` : ""}${
      storeId && storeId !== "0" ? `&store_id=${storeId}` : ""
    }${filterCashier && filterCashier !== "ALL_CASHIERS" ? `&cashier_name=${encodeURIComponent(filterCashier)}` : ""}`;
    
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${process.env.REACT_APP_API_URL}${url}`,
      responseType: "arraybuffer",
      headers,
      onDownloadProgress: (progressEvent) => {
        const percentage = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        setDownloadProgress(percentage);
      },
    };

    try {
      const response = await axios(config);
      const outputFilename =
        user_type !== "merchant"
          ? `Laporan Closing Kasir - ${selected_store.name}${
              filterCashier && filterCashier !== "ALL_CASHIERS" ? ` - ${filterCashier}` : ""
            }${
              filterPayload.start_date
                ? " - " + moment(filterPayload.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
                : ""
            }${
              filterPayload?.end_date
                ? " - " + moment(filterPayload.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
                : ""
            }.xlsx`
          : `Laporan Closing Kasir - ${profile_data.name}${
              filterStore && filterStore.store_name ? " - " + filterStore.store_name : ""
            }${
              filterCashier && filterCashier !== "ALL_CASHIERS" ? " - " + filterCashier : ""
            }${
              filterPayload.start_date
                ? " - " + moment(filterPayload.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
                : ""
            }${
              filterPayload?.end_date
                ? " - " + moment(filterPayload.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
                : ""
            }${
              !startDate && !endDate ? " - " + moment().format('YYYY-MM-DD') : ""
            }.xlsx`;

      const urlBlob = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = urlBlob;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(urlBlob);
    } catch (error) {
      toast.error("Error downloading report");
      setDownload(false);
    }
  };

  const isInitialMount = useRef(true);
  const prevFilterRef = useRef({ filterStore, filterCashier, filter });

  useEffect(() => {
    const currentFilter = { filterStore, filterCashier, filter };
    
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchTransactions();
      return;
    }

    const hasFilterChanged = 
      JSON.stringify(prevFilterRef.current.filterStore) !== JSON.stringify(currentFilter.filterStore) ||
      prevFilterRef.current.filterCashier !== currentFilter.filterCashier ||
      prevFilterRef.current.filter !== currentFilter.filter;

    if (hasFilterChanged) {
      prevFilterRef.current = currentFilter;
      if (offset !== 0) {
        setOffset(0);
        return; 
      }
    }

    const cashierValidation = availableCashiers.includes(filterCashier) || filterCashier === "ALL_CASHIERS";
    if (cashierValidation) {
      fetchTransactions();
    }
  }, [limit, offset, filterStore, filter, filterCashier, availableCashiers]);


  useEffect(() => {
    if (downloadProgress === 100) {
      setTimeout(() => {
        setDownload(false);
        setDownloadProgress(0);
      }, 500);
    }
  }, [downloadProgress]);

  const truncateText = (text: string, maxLength: number = 20) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const columns = [
    {
      name: "Nama Kasir",
      selector: (row: any) => (
        <Tooltip title={row.cashier_name} placement="top">
          <span>{truncateText(row.cashier_name)}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      wrap: true,
      name: "Waktu Buka Kasir",
      selector: (row: any) => moment(row.start_time).format('DD-MM-YYYY HH:mm:ss'),
      sortable: true,
    },
    {
      wrap: true,
      name: "Waktu Tutup Kasir",
      selector: (row: any) => moment(row.end_time).format('DD-MM-YYYY HH:mm:ss'),
      sortable: true,
    },
    {
      name: "Kode Sesi",
      cell: (row: any) => row.session_code,
      sortable: true,
    },
    {
      name: "Initial Cash",
      cell: (row: any) => formatCurrency(Number(row.initial_cash)),
      sortable: true,
    },
    {
      name: "Closing Cash (by System)",
      cell: (row: any) => formatCurrency(Number(row.closing_cash_system)),
      sortable: true,
    },
    {
      name: "Closing Cash (by Kasir)",
      cell: (row: any) => formatCurrency(Number(row.closing_cash_cashier)),
      sortable: true,
    },
    {
      name: "Margin Cash",
      cell: (row: any) => formatCurrency(Number(row.margin_cash)),
      sortable: true,
    },
    {
      name: "Notes",
      cell: (row: any) => row.notes,
    },
  ];

  return (
    <MerchantLayout>
      <OrderGroups tabActive={2}>
        <Box sx={{ background: "white", borderRadius: "10px" }} padding={"20px"}>
          <NormalText fontSize="20px" fontWeight="bold" margin={"0 0 20px 0"}>
            Filter
          </NormalText>
          <Grid container>
            <Grid item md={2} xs={12} sm={12}>
              <NormalText fontSize="15px" fontWeight="bold">Pilih Tanggal</NormalText>
            </Grid>
            <Grid item md={6}>
              <Stack direction={'row'} spacing={2} alignItems={'center'}>
                <InputDate
                  value={filterPayload.start_date}
                  disableFuture
                  onChange={(v: any) => {
                    setFilterPayload((prev) => ({
                      ...prev,
                      start_date: v,
                      end_date: moment(v, "DD-MM-YYYY").isAfter(moment(prev.end_date, "DD-MM-YYYY"))
                        ? v
                        : prev.end_date,
                    }));
                  }}
                />
                <InputDate
                  disableFuture
                  value={filterPayload.end_date}
                  minDate={moment(filterPayload.start_date, "DD-MM-YYYY").toDate()}
                  onChange={(v: any) => {
                    if (moment(v, "DD-MM-YYYY").isBefore(moment(filterPayload.start_date, "DD-MM-YYYY"))) {
                      toast.warning('Tanggal akhir tidak boleh sebelum tanggal awal');
                    } else {
                      setFilterPayload({ ...filterPayload, end_date: v });
                    }
                  }}
                />
              </Stack>
            </Grid>
          </Grid>

          {user_type && user_type === "merchant" && (
            <FilterStore
              value={filterStore}
              onChange={(val: any) => {
                setFilterStore(val);
                setFilterCashier("ALL_CASHIERS");
              }}
              filterType={"report"}
              stores={stores}
            />
          )}

          <Grid container margin={"20px 0 0 0"}>
            <Grid item xs={12} sm={12} md={2}>
              <NormalText fontSize="15px" fontWeight="bold">
                Pilih Nama Kasir
              </NormalText>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Autocomplete
                id="combo-box-cashier-list"
                options={["ALL_CASHIERS", ...availableCashiers]}
                getOptionLabel={(option) =>
                  option === "ALL_CASHIERS" ? "Semua Kasir" : option
                }
                value={filterCashier}
                onChange={(event: any, newValue: any) => {
                  setFilterCashier(newValue || "ALL_CASHIERS");
                }}
                sx={{ width: "250px" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    style={{ fontSize: 12 }}
                    size="small"
                    placeholder="Pilih Kasir"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Tooltip
                      title={option === "ALL_CASHIERS" ? "Semua Kasir" : option}
                      placement="right"
                    >
                      <span>
                        {truncateText(
                          option === "ALL_CASHIERS" ? "Semua Kasir" : option,
                          30
                        )}
                      </span>
                    </Tooltip>
                  </Box>
                )}
              />
            </Grid>
          </Grid>

          <Grid container margin={"20px 0 0 0"}>
            <Grid item xs={12} sm={12} md={2}>
              <NormalText fontSize="15px" fontWeight="bold">
                Cari Nomor Sesi
              </NormalText>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box width={"250px"}>
                <Search>
                  <StyledInputBase
                    placeholder="Search Nomor Sesi"
                    inputProps={{ "aria-label": "search" }}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </Search>
              </Box>
            </Grid>
          </Grid>

          <Grid container margin={"20px 0 0 0"}>
            <Grid item xs={12} sm={12} md={2}></Grid>
            <Grid item xs={12} sm={12} md={10}>
              <Stack direction={"row"}>
                <Button variant="primary" onClick={() => fetchTransactions()}>
                  Filter
                </Button>
                <Button
                  variant="primary"
                  background="#21a421 !important"
                  margin="0 0 0 10px"
                  onClick={() => handleExport()}
                  disabled={download === true}
                >
                  Download Excel
                </Button>
                {download && (
                  <div className="ml-3">
                    <Typography fontSize={"0.8rem"} className="mb-1">
                      Mempersiapkan laporan anda ...
                    </Typography>
                    <div
                      className="progress"
                      role="progressbar"
                      aria-label="Animated striped example"
                    >
                      <div
                        className="progress-bar progress-bar-striped bg-success progress-bar-animated"
                        style={{
                          width: `${downloadProgress ? downloadProgress : 0}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </Stack>
            </Grid>
          </Grid>

          <Tables
            progressPending={loading}
            hasContainer={false}
            records={
              transactions && transactions.data && transactions.data.length > 0
                ? transactions.data
                : []
            }
            columns={columns}
            totalRow={transactions ? transactions.meta.total : 0}
            paginationPerPage={limit}
            onChangePage={(page) => setOffset(page - 1)}
            onChangeRowsPerPage={(currentRowsPerPage) =>
              setLimit(currentRowsPerPage)
            }
          />
        </Box>
      </OrderGroups>
    </MerchantLayout>
  );
};

export default TransactionReportPage;
